<script lang="ts">
  import { Link } from 'svelte-routing';
  import { getModeByUrlFragments } from '@atlasguess/games';
  import { withPageTitle } from '../lib/page-title';
  import ModeFrame from '../games/framework/ModeFrame.svelte';
  import ClickDistancePractice from '../games/modes/ClickDistancePractice.svelte';
  import ClickDistanceDaily from '../games/modes/ClickDistanceDaily.svelte';
  import ClickInRegionPractice from '../games/modes/ClickInRegionPractice.svelte';
  import ClickInRegionQuiz from '../games/modes/ClickInRegionQuiz.svelte';

  interface Props {
    /** From `/play/:scope/:format` — svelte-routing passes path params as props. */
    scope?: string;
    format?: string;
    /**
     * Optional 4th URL segment from `/play/:scope/:format/:filter` — a
     * continent slug (`europe`) or a country slug (`cote-d-ivoire`) that
     * narrows the active pool. Resolved inside each mode component
     * against `getCountryList()` because the slug → criteria mapping
     * needs the dataset.
     */
    filter?: string;
  }

  let { scope = '', format = '', filter }: Props = $props();

  const mode = $derived(getModeByUrlFragments(scope, format));

  // Modes that hide the surface sphere don't need the photo texture.
  // Save the bandwidth and the decode time by skipping the fetch entirely.
  const skipTexture = $derived(mode ? !mode.layers.texture.visible : false);

  // Title tracks the active mode so browser tabs / history are scannable.
  // Falls back to "Play" when the URL doesn't resolve to a known mode
  // (the {#if mode} below already handles the not-found UI).
  $effect(() => withPageTitle(mode?.displayName ?? 'Play'));
</script>

{#if mode}
  <!-- Re-mount ModeFrame whenever the mode changes — the engine fully
       tears down and reconstructs, guaranteeing clean fills/markers/
       camera/texture state. Avoids cross-mode handoff race conditions
       where an old mode's async initialize was still writing to the
       engine when the new mode mounted, AND fixes the
       skip-texture-locked-at-construction bug (going from a no-texture
       mode to a texture mode would otherwise show a black globe).
       The engine setup cost (~300-700ms for country modes including
       polygon decode + fills triangulation) is hidden behind ModeFrame's
       loading veil on each swap. -->
  <!-- Re-mount key includes the filter slug so flipping the URL filter
       (FilterPicker → navigate) tears down + rebuilds the engine the
       same way a full mode swap does. Any cleaner approach would need
       the mode components to subscribe to filter changes themselves
       and re-run their pool / dot / inactive-state logic; the cost
       of a re-mount is hidden by ModeFrame's loading veil and avoids
       a whole class of partial-state bugs. -->
  {#key `${mode.id}:${filter ?? ''}`}
    <ModeFrame {skipTexture}>
      {#snippet children(engine, markReady)}
        <!-- Every variant of the GameModeConfig discriminated union should
             be handled here. TypeScript narrows `mode` to `never` after the
             last branch — adding a new mode type to the schema without a
             component will fail at this site, which is what we want. -->
        {#if mode.interaction === 'click-distance' && mode.format === 'practice'}
          <ClickDistancePractice config={mode} {engine} {filter} />
        {:else if mode.interaction === 'click-distance' && mode.format === 'daily'}
          <ClickDistanceDaily config={mode} {engine} />
        {:else if mode.interaction === 'click-in-region' && mode.format === 'practice'}
          <ClickInRegionPractice config={mode} {engine} {markReady} {filter} />
        {:else if mode.interaction === 'click-in-region' && mode.format === 'quiz'}
          <ClickInRegionQuiz config={mode} {engine} {markReady} />
        {/if}
      {/snippet}
    </ModeFrame>
  {/key}
{:else}
  <div class="play-stub">
    <div class="play-stub__name">Mode not found</div>
    <div class="play-stub__pending">No mode registered at /play/{scope}/{format}.</div>
    <Link to="/">Go home</Link>
  </div>
{/if}
